import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  User,
  Building2,
  Mail,
  Phone,
  Calendar,
  FileText
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import ServiceAAdminPanel from "@/components/admin/ServiceAAdminPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminEngagementDetail - Detailed view of a single engagement (SER)
 * 
 * Shows:
 * - Client information
 * - Engagement status
 * - Service A workflow panel (if SERVICE_A)
 */
const AdminEngagementDetail = () => {
  const { serId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [engagement, setEngagement] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEngagement = useCallback(async () => {
    try {
      // Get SER details
      const serResponse = await axios.get(`${API}/admin/ser/${serId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEngagement(serResponse.data);

      // Get client/user details if user_id exists
      if (serResponse.data.user_id) {
        try {
          const userResponse = await axios.get(`${API}/admin/users/${serResponse.data.user_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setClient(userResponse.data);
        } catch (e) {
          console.log("Could not fetch user details");
        }
      }
    } catch (error) {
      console.error("Failed to fetch engagement:", error);
      toast.error("Failed to load engagement details");
    } finally {
      setLoading(false);
    }
  }, [serId, token]);

  useEffect(() => {
    fetchEngagement();
  }, [fetchEngagement]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Engagement not found</p>
        <Button variant="outline" onClick={() => navigate("/admin/engagements")} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Engagements
        </Button>
      </div>
    );
  }

  const statusColors = {
    ACTIVE: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    ONBOARDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    COMPLETE: "bg-green-500/20 text-green-400 border-green-500/30",
    CLOSED: "bg-slate-500/20 text-slate-400 border-slate-500/30"
  };

  return (
    <div className="space-y-6" data-testid="admin-engagement-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/admin/engagements")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Engagement Details</h1>
            <p className="text-sm text-slate-400">{serId}</p>
          </div>
        </div>
        <Badge className={statusColors[engagement.lifecycle_state] || statusColors.ACTIVE}>
          {engagement.lifecycle_state || "ACTIVE"}
        </Badge>
      </div>

      {/* Client Info Card */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Client Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-400">Business Name</p>
              <p className="text-white font-medium flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                {client?.business_name || engagement.client_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Contact</p>
              <p className="text-white font-medium">
                {client ? `${client.first_name || ""} ${client.last_name || ""}`.trim() : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Email</p>
              <p className="text-white font-medium flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                {client?.email || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Phone</p>
              <p className="text-white font-medium flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" />
                {client?.telephone || "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engagement Overview */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Engagement Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-400">Service</p>
              <p className="text-white font-medium">{engagement.service_name || engagement.service_id || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Service ID</p>
              <Badge variant="outline">{engagement.service_id || "N/A"}</Badge>
            </div>
            <div>
              <p className="text-sm text-slate-400">Created</p>
              <p className="text-white font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                {engagement.created_at ? new Date(engagement.created_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Last Updated</p>
              <p className="text-white font-medium">
                {engagement.updated_at ? new Date(engagement.updated_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service A Workflow Panel */}
      {(engagement.service_id === "SERVICE_A" || engagement.service_id === "AIMB-A") && (
        <ServiceAAdminPanel serId={serId} onUpdate={fetchEngagement} />
      )}

      {/* For non-Service-A engagements, show basic info */}
      {engagement.service_id !== "SERVICE_A" && engagement.service_id !== "AIMB-A" && (
        <Card className="bg-navy-900/50 border-slate-800">
          <CardContent className="py-8 text-center">
            <p className="text-slate-400">
              Detailed workflow panel not yet available for {engagement.service_id}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminEngagementDetail;
